/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import * as types from "../../types/primitives.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Websocket event type
 */
export const CompactReadingEventType = {
  AlarmActivation: "alarm_activation",
  Error: "error",
  Keepalive: "keepalive",
  Reading: "reading",
  Setting: "setting",
  ShutdownBanner: "shutdown_banner",
  Trends: "trends",
  UserActivity: "user_activity",
} as const;
/**
 * Websocket event type
 */
export type CompactReadingEventType = OpenEnum<typeof CompactReadingEventType>;

/**
 * Values of numerous readings at one point in time
 */
export type CompactReadingEvent = {
  /**
   * The trace metadata, initialized by the trace stream interceptor
   */
  traceMetadata?: { [k: string]: string } | undefined;
  /**
   * Boolean-valued readings
   */
  booleans?: { [k: string]: boolean } | undefined;
  /**
   * Enum-valued readings
   */
  enums?: { [k: string]: string } | undefined;
  /**
   * Null-valued readings
   */
  nulls?: Array<string> | undefined;
  /**
   * Number-valued readings
   */
  numbers?: { [k: string]: number } | undefined;
  /**
   * Unscaled number-valued readings. Present only if raws=true in the payload.
   */
  raws?: { [k: string]: number } | undefined;
  /**
   * Timestamp of the latest values
   */
  timestamp: Date;
  /**
   * Websocket event type
   */
  type?: CompactReadingEventType | undefined;
};

/** @internal */
export const CompactReadingEventType$inboundSchema: z.ZodType<
  CompactReadingEventType,
  z.ZodTypeDef,
  unknown
> = openEnums.inboundSchema(CompactReadingEventType);
/** @internal */
export const CompactReadingEventType$outboundSchema: z.ZodType<
  string,
  z.ZodTypeDef,
  CompactReadingEventType
> = openEnums.outboundSchema(CompactReadingEventType);

/** @internal */
export const CompactReadingEvent$inboundSchema: z.ZodType<
  CompactReadingEvent,
  z.ZodTypeDef,
  unknown
> = z.object({
  TraceMetadata: types.optional(z.record(types.string())),
  booleans: types.optional(z.record(types.boolean())),
  enums: types.optional(z.record(types.string())),
  nulls: types.optional(z.array(types.string())),
  numbers: types.optional(z.record(types.number())),
  raws: types.optional(z.record(types.number())),
  timestamp: types.date(),
  type: CompactReadingEventType$inboundSchema.default("reading"),
}).transform((v) => {
  return remap$(v, {
    "TraceMetadata": "traceMetadata",
  });
});
/** @internal */
export type CompactReadingEvent$Outbound = {
  TraceMetadata?: { [k: string]: string } | undefined;
  booleans?: { [k: string]: boolean } | undefined;
  enums?: { [k: string]: string } | undefined;
  nulls?: Array<string> | undefined;
  numbers?: { [k: string]: number } | undefined;
  raws?: { [k: string]: number } | undefined;
  timestamp: string;
  type: string;
};

/** @internal */
export const CompactReadingEvent$outboundSchema: z.ZodType<
  CompactReadingEvent$Outbound,
  z.ZodTypeDef,
  CompactReadingEvent
> = z.object({
  traceMetadata: z.record(z.string()).optional(),
  booleans: z.record(z.boolean()).optional(),
  enums: z.record(z.string()).optional(),
  nulls: z.array(z.string()).optional(),
  numbers: z.record(z.number()).optional(),
  raws: z.record(z.number()).optional(),
  timestamp: z.date().transform(v => v.toISOString()),
  type: CompactReadingEventType$outboundSchema.default("reading"),
}).transform((v) => {
  return remap$(v, {
    traceMetadata: "TraceMetadata",
  });
});

export function compactReadingEventToJSON(
  compactReadingEvent: CompactReadingEvent,
): string {
  return JSON.stringify(
    CompactReadingEvent$outboundSchema.parse(compactReadingEvent),
  );
}
export function compactReadingEventFromJSON(
  jsonString: string,
): SafeParseResult<CompactReadingEvent, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => CompactReadingEvent$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'CompactReadingEvent' from JSON`,
  );
}
